import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { AlertTriangle } from 'lucide-react'

import { FbModal } from 'components/UI/Modal'
import { BaseTextInput } from 'components/FormUtils/TextInput'
import useApiService from 'services/api'
import type { Reply, ReplyContactEmailCategory } from './replies_api'
import { useUpdateContactFromReply } from './replies_api'

interface MismatchEmailModalProps {
  reply: Reply
  /** The CRM Contact this reply resolves to. Loaded via `useGetContactDetails`
   * — caller is responsible for only mounting this modal when the badge is
   * shown (i.e. reply.contact_id is non-null). */
  contactId: number
  show: boolean
  onClose: () => void
}

const CATEGORY_ORDER: ReplyContactEmailCategory[] = [
  'work',
  'personal',
  'generic',
]
const CATEGORY_LABEL: Record<ReplyContactEmailCategory, string> = {
  work: 'Work',
  personal: 'Personal',
  generic: 'Other',
}

function parseFromName(
  fromName: string | null
): { first: string; last: string } | null {
  const trimmed = (fromName ?? '').trim()
  if (!trimmed) return null
  const parts = trimmed.split(/\s+/)
  return {
    first: parts[0] ?? '',
    last: parts.slice(1).join(' '),
  }
}

export function MismatchEmailModal({
  reply,
  contactId,
  show,
  onClose,
}: MismatchEmailModalProps) {
  const api = useApiService()
  const { data: contact } = api.useGetContactDetails(contactId, undefined, show)

  if (!contact) {
    // Still loading the first time the modal opens. Render nothing rather
    // than flashing an empty modal — useGetContactDetails caches subsequently
    // so reopens are instant.
    return null
  }

  return (
    <MismatchEmailModalLoaded
      reply={reply}
      contact={contact}
      show={show}
      onClose={onClose}
    />
  )
}

function MismatchEmailModalLoaded({
  reply,
  contact,
  show,
  onClose,
}: {
  reply: Reply
  contact: {
    id: number
    first_name: string
    last_name: string
    title: string
    email_work: string | null
    email_personal: string | null
    email_generic: string | null
  }
  show: boolean
  onClose: () => void
}) {
  const contactEmails: Record<ReplyContactEmailCategory, string | null> = {
    work: contact.email_work,
    personal: contact.email_personal,
    generic: contact.email_generic,
  }

  // Default category: first empty slot in work → personal → generic order;
  // null if all three are full and the operator must explicitly pick a slot
  // to overwrite.
  const defaultCategory = useMemo<ReplyContactEmailCategory | null>(() => {
    const empty = CATEGORY_ORDER.find((cat) => !contactEmails[cat])
    return empty ?? null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact.id])

  const [category, setCategory] = useState<ReplyContactEmailCategory | null>(
    defaultCategory
  )
  const [firstName, setFirstName] = useState(contact.first_name ?? '')
  const [lastName, setLastName] = useState(contact.last_name ?? '')
  const [title, setTitle] = useState(contact.title ?? '')

  const fromNameParsed = useMemo(
    () => parseFromName(reply.from_name),
    [reply.from_name]
  )
  const showFromNameSuggestion =
    fromNameParsed != null &&
    (fromNameParsed.first !== firstName || fromNameParsed.last !== lastName)

  const mutation = useUpdateContactFromReply()

  const onSave = () => {
    if (category == null) return
    const payload = {
      email: reply.from_email_address,
      category,
      ...(firstName !== (contact.first_name ?? '')
        ? { first_name: firstName }
        : {}),
      ...(lastName !== (contact.last_name ?? '')
        ? { last_name: lastName }
        : {}),
      ...(title !== (contact.title ?? '') ? { title } : {}),
    }
    mutation.mutate(
      { contactId: contact.id, payload },
      {
        onSuccess: () => {
          toast.success('Contact updated')
          onClose()
        },
        onError: () => {
          toast.error('Failed to update contact')
        },
      }
    )
  }

  return (
    <FbModal
      open={show}
      onOpenChange={(o: boolean) => {
        if (!o) onClose()
      }}
      title={
        <span className="flex items-center gap-2">
          <AlertTriangle className="size-5 text-brand" />
          Reply from a different address
        </span>
      }
      description={`Update ${contact.first_name} ${contact.last_name}'s contact`.trim()}
      acceptButtonText="Save"
      cancelButtonText="Cancel"
      onAccept={onSave}
      onCancel={onClose}
      blockAccept={category == null || mutation.isPending}
    >
      <div className="flex flex-col gap-3 px-1">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-fg-secondary">
            New email
          </span>
          <div className="rounded border border-line bg-surface-hover px-3 py-2 text-sm text-fg">
            {reply.from_email_address}
          </div>
        </div>

        <fieldset className="flex flex-col gap-2">
          <legend className="text-xs font-medium text-fg-secondary">
            Save as
          </legend>
          {CATEGORY_ORDER.map((cat) => {
            const existing = contactEmails[cat]
            return (
              <label
                key={cat}
                className="flex items-center gap-2 cursor-pointer rounded border border-line px-3 py-2 text-sm hover:bg-surface-hover"
              >
                <input
                  type="radio"
                  name="mismatch-category"
                  value={cat}
                  checked={category === cat}
                  onChange={() => setCategory(cat)}
                  className="size-4"
                />
                <span className="font-medium text-fg">
                  {CATEGORY_LABEL[cat]}
                </span>
                <span className="text-fg-muted">{existing ?? '(empty)'}</span>
                {existing && (
                  <span className="ml-auto inline-flex items-center gap-1 text-xs text-warning-strong">
                    <AlertTriangle className="size-3" />
                    will replace
                  </span>
                )}
              </label>
            )
          })}
        </fieldset>

        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium text-fg-secondary">
            Name (optional)
          </span>
          <div className="grid grid-cols-2 gap-2">
            <BaseTextInput
              name="first_name"
              value={firstName}
              onChange={(v) => setFirstName(String(v))}
              placeholder="First name"
              noMargin
            />
            <BaseTextInput
              name="last_name"
              value={lastName}
              onChange={(v) => setLastName(String(v))}
              placeholder="Last name"
              noMargin
            />
          </div>
          {showFromNameSuggestion && fromNameParsed && (
            <button
              type="button"
              className="text-left text-xs text-brand hover:underline"
              onClick={() => {
                setFirstName(fromNameParsed.first)
                setLastName(fromNameParsed.last)
              }}
            >
              ↳ Use &ldquo;{reply.from_name}&rdquo; from reply
            </button>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-fg-secondary">
            Title (optional)
          </span>
          <BaseTextInput
            name="title"
            value={title}
            onChange={(v) => setTitle(String(v))}
            placeholder="Title"
            noMargin
          />
        </div>
      </div>
    </FbModal>
  )
}
